import React from 'react';
import { Document, Page, View, Text, StyleSheet, Image } from '@react-pdf/renderer';
import {
  MovimentacaoCaixa,
  CategoriaEntradaCaixa,
  CategoriaSaidaCaixa,
  CATEGORIA_ENTRADA_LABEL,
  CATEGORIA_SAIDA_LABEL,
  movimentacaoAfetaSaldoCaixa,
} from '../../types';

const toNum = (v: unknown): number => {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  const s = String(v).trim();
  if (s === '') return 0;
  if (/,\d*$/.test(s)) return Number(s.replace(/\./g, '').replace(',', '.')) || 0;
  return parseFloat(s) || 0;
};

const formatCurrency = (value: unknown): string =>
  toNum(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDateBR = (dateString?: string): string => {
  if (!dateString) return '—';
  const d = String(dateString).split('T')[0];
  const [y, m, day] = d.split('-');
  if (!y || !m || !day) return d;
  return `${day}/${m}/${y}`;
};

const truncate = (v: unknown, max: number): string => {
  const s = String(v ?? '').replace(/\s+/g, ' ').trim();
  if (!s) return '';
  if (s.length <= max) return s;
  return `${s.slice(0, Math.max(0, max - 1)).trimEnd()}…`;
};

/** Quebra palavras longas para não estourar colunas no react-pdf. */
const breakLongWords = (text: string, chunkSize = 24): string => {
  if (!text) return text;
  return text
    .split(' ')
    .map((token) => {
      if (token.length <= chunkSize) return token;
      const parts: string[] = [];
      for (let i = 0; i < token.length; i += chunkSize) parts.push(token.slice(i, i + chunkSize));
      return parts.join(' ');
    })
    .join(' ');
};

const getCategoryLabel = (m: MovimentacaoCaixa): string => {
  if (!m.category) return '—';
  if (m.type === 'entrada') {
    return CATEGORIA_ENTRADA_LABEL[m.category as CategoriaEntradaCaixa] ?? String(m.category);
  }
  return CATEGORIA_SAIDA_LABEL[m.category as CategoriaSaidaCaixa] ?? String(m.category);
};

const getCashLabel = (m: MovimentacaoCaixa): string => {
  if (m.type === 'entrada') return 'Entra';
  return movimentacaoAfetaSaldoCaixa(m) ? 'Debitado' : 'Pendente';
};

const styles = StyleSheet.create({
  page: { padding: 32, fontSize: 10, fontFamily: 'Helvetica', color: '#111827' },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 14, gap: 12, alignItems: 'center' },
  logo: { width: 140, height: 36, objectFit: 'contain' },
  title: { fontSize: 16, fontWeight: 700 },
  subtitle: { marginTop: 4, color: '#6B7280' },
  totalsBox: { borderWidth: 1, borderColor: '#E5E7EB', borderRadius: 8, padding: 10, marginBottom: 14 },
  totalsRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 6 },
  totalsLabel: { color: '#6B7280' },
  totalsValue: { fontWeight: 700 },
  table: { borderWidth: 1, borderColor: '#E5E7EB', borderRadius: 8, overflow: 'hidden' },
  rowHeader: { flexDirection: 'row', backgroundColor: '#F9FAFB', borderBottomWidth: 1, borderBottomColor: '#E5E7EB', paddingVertical: 8, paddingHorizontal: 8 },
  row: { flexDirection: 'column', borderBottomWidth: 1, borderBottomColor: '#F3F4F6', paddingVertical: 7, paddingHorizontal: 8 },
  rowTop: { flexDirection: 'row', alignItems: 'flex-start' },
  cellDate: { width: 62 },
  cellDue: { width: 62 },
  cellType: { width: 44 },
  cellCash: { width: 56 },
  cellCat: { width: 92 },
  cellDesc: { flexGrow: 1, minWidth: 0, paddingRight: 8 },
  cellValue: { width: 78, textAlign: 'right', fontWeight: 700 },
  noteText: { fontSize: 9, lineHeight: 1.2, color: '#6B7280' },
  descText: { fontSize: 10, lineHeight: 1.25 },
});

export interface MovimentacoesReportPDFProps {
  movimentacoes: MovimentacaoCaixa[];
  generatedAtLabel: string;
  logoUrl?: string | null;
  title?: string;
  subtitle?: string;
}

const MovimentacoesReportPDF: React.FC<MovimentacoesReportPDFProps> = ({
  movimentacoes,
  generatedAtLabel,
  logoUrl,
  title,
  subtitle,
}) => {
  const sorted = [...movimentacoes].sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));

  const totalEntradas = sorted.reduce((sum, m) => sum + (m.type === 'entrada' ? toNum(m.amount) : 0), 0);
  const totalSaidasAll = sorted.reduce((sum, m) => sum + (m.type === 'saida' ? toNum(m.amount) : 0), 0);
  const totalSaidasDebitado = sorted.reduce(
    (sum, m) => sum + (m.type === 'saida' && movimentacaoAfetaSaldoCaixa(m) ? toNum(m.amount) : 0),
    0
  );
  const totalSaidasPendentes = sorted.reduce(
    (sum, m) => sum + (m.type === 'saida' && !movimentacaoAfetaSaldoCaixa(m) ? toNum(m.amount) : 0),
    0
  );

  return (
    <Document title="Relatório de movimentações">
      <Page size="A4" style={styles.page}>
        <View style={styles.headerRow}>
          {logoUrl ? <Image src={logoUrl} style={styles.logo} /> : <View />}
          <View>
            <Text style={styles.title}>{title || 'Movimentações'}</Text>
            <Text style={styles.subtitle}>Gerado em: {generatedAtLabel}</Text>
            {subtitle ? <Text style={styles.subtitle}>{subtitle}</Text> : null}
          </View>
          <View>
            <Text style={styles.subtitle}>Total itens: {sorted.length}</Text>
          </View>
        </View>

        <View style={styles.totalsBox}>
          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>Total de entradas:</Text>
            <Text style={styles.totalsValue}>{formatCurrency(totalEntradas)}</Text>
          </View>
          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>Total de gastos (saídas):</Text>
            <Text style={styles.totalsValue}>{formatCurrency(totalSaidasAll)}</Text>
          </View>
          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>Saídas debitadas (afetam caixa):</Text>
            <Text style={styles.totalsValue}>{formatCurrency(totalSaidasDebitado)}</Text>
          </View>
          <View style={styles.totalsRow}>
            <Text style={styles.totalsLabel}>Saídas pendentes (contas a pagar):</Text>
            <Text style={styles.totalsValue}>{formatCurrency(totalSaidasPendentes)}</Text>
          </View>
        </View>

        <View style={styles.table}>
          <View style={styles.rowHeader}>
            <Text style={styles.cellDate}>Data</Text>
            <Text style={styles.cellDue}>Venc.</Text>
            <Text style={styles.cellType}>Tipo</Text>
            <Text style={styles.cellCash}>Caixa</Text>
            <Text style={styles.cellCat}>Categoria</Text>
            <Text style={styles.cellValue}>Valor</Text>
          </View>

          {sorted.map((m) => (
            <View key={m.id} style={styles.row} wrap={false}>
              <View style={styles.rowTop}>
                <Text style={styles.cellDate}>{formatDateBR(m.date)}</Text>
                <Text style={styles.cellDue}>{formatDateBR(m.dueDate)}</Text>
                <Text style={styles.cellType}>{m.type === 'entrada' ? 'Ent.' : 'Saída'}</Text>
                <Text style={styles.cellCash}>{getCashLabel(m)}</Text>
                <Text style={styles.cellCat}>{getCategoryLabel(m)}</Text>
                <Text style={styles.cellValue}>
                  {m.type === 'entrada' ? '+' : '−'} {formatCurrency(m.amount)}
                </Text>
              </View>

              <View style={{ marginTop: 3 }}>
                <Text style={styles.descText}>{breakLongWords(truncate(m.description || '—', 220))}</Text>
                {m.payableNote ? (
                  <Text style={styles.noteText}>Lembrete: {breakLongWords(truncate(m.payableNote, 140))}</Text>
                ) : null}
              </View>
            </View>
          ))}
        </View>
      </Page>
    </Document>
  );
};

export default MovimentacoesReportPDF;

